import axios from 'axios';
import { getApiBaseUrl } from './apiConfig';

/**
 * Trigger a manual sync of Slack data
 * @param {Object} organization - The organization to sync data for
 * @returns {Promise} Promise that resolves with the sync status
 */
export const triggerSlackSync = async (organization) => {
  if (!organization) {
    throw new Error('Missing required organization parameter');
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    
    console.log('Triggering event processing...');
    
    const response = await axios.post(
      `${apiUrl}/cron/trigger-event-processing`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }
    );
    
    console.log('Event processing response:', response.data);
    
    if (response.data.success) {
      return {
        success: true,
        syncStarted: true,
        syncId: null,
        message: response.data.message,
        syncComplete: false
      };
    }
    
    return {
      success: false,
      message: 'Failed to trigger event processing'
    };
  } catch (error) {
    console.error('Error triggering event processing:', error);
    throw error;
  }
};

/**
 * Check the sync status for an organization
 * @param {Object} organization - The organization to check sync status for
 * @returns {Promise} Promise that resolves with the sync status
 */
export const checkSyncStatus = async (organization) => {
  if (!organization) {
    return {
      lastSynced: null,
      hasReports: false,
      reportCount: 0,
      syncStatus: 'unknown'
    };
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    console.log(`Checking sync status for organization ${orgId}`);
    
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}/sync-status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }
    );
    
    if (response.data.success) {
      console.log('Sync status response:', response.data);
      return {
        lastSynced: response.data.data.lastSynced ? new Date(response.data.data.lastSynced) : null,
        hasReports: response.data.data.hasReports,
        reportCount: response.data.data.reportCount || 0,
        syncStatus: response.data.data.syncStatus || 'unknown',
        lastError: response.data.data.lastError
      };
    }
    
    return {
      lastSynced: null,
      hasReports: false,
      reportCount: 0,
      syncStatus: 'unknown'
    };
  } catch (error) {
    console.error('Error checking sync status:', error);
    return {
      lastSynced: null,
      hasReports: false,
      reportCount: 0,
      syncStatus: 'error',
      lastError: error.message
    };
  }
};

/**
 * Get a filtered report for a specific organization, date range, and channel
 * @param {Object} organization - The organization to get the report for
 * @param {Object} dateRange - Date range to get the report for
 * @param {String} channelId - Optional channel ID to filter by
 * @returns {Promise} Promise that resolves with the report data
 */
export const getFilteredReport = async (organization, dateRange, channelId = null) => {
  if (!organization || !dateRange || !dateRange.range) {
    throw new Error('Missing required parameters for getting report');
  }
  
  try {
    const token = localStorage.getItem('token');
    const apiUrl = getApiBaseUrl();
    const orgId = organization._id || organization.id;
    
    const params = {
      rangeType: dateRange.range
    };
    
    if (channelId) {
      params.channelId = channelId;
    }
    
    const response = await axios.get(
      `${apiUrl}/slack-reports/organization/${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      }
    );
    
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        lastSynced: new Date(response.data.lastSynced)
      };
    }
    
    return {
      success: false,
      message: 'Failed to get report'
    };
  } catch (error) {
    // Handle 404 (no report found) without logging an error
    if (error.response && error.response.status === 404) {
      return {
        success: false,
        message: 'No report found for this date range'
      };
    }
    
    throw error;
  }
}; 

export const slackSyncService = {
  async triggerSync(dateRange) {
    try {
      const organizationId = localStorage.getItem('organizationId');
      if (!organizationId) {
        console.error('No organization ID found');
        throw new Error('No organization ID found');
      }

      // Debug the date range
      console.log('Original date range:', dateRange);
      
      // Fix future dates issue
      const now = new Date();
      let fixedDateRange = { ...dateRange };
      
      // If dates are in the future, adjust them to be in the past
      if (dateRange.startDate > now || dateRange.endDate > now) {
        console.log('Fixing future dates in date range');
        
        // Set end date to now
        fixedDateRange.endDate = now;
        
        // Calculate the duration of the original range
        const duration = dateRange.endDate - dateRange.startDate;
        
        // Set start date to (now - duration)
        fixedDateRange.startDate = new Date(now.getTime() - duration);
        
        console.log('Fixed date range:', fixedDateRange);
      }
      
      console.log(`Triggering sync for organization ${organizationId} with date range:`, fixedDateRange);
      
      const response = await fetch(`/api/integrations/slack/sync/${organizationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(fixedDateRange)
      });

      const data = await response.json();
      console.log('Sync response:', data);
      return data;
    } catch (error) {
      console.error('Error triggering Slack sync:', error);
      throw error;
    }
  },
  
  // Add more methods as needed
}; 